import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Generations from "./pages/Generations";
import OrderConfirmation from "./pages/OrderConfirmation";
import AdminStudio from "./pages/AdminStudio";
import NotFound from "./pages/NotFound";
import Header from "./components/layout/Header";
import { AppProvider } from "@/contexts/AppContext";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import CookieConsent from "@/components/shared/CookieConsent";
import CreateLayout from "./pages/create/CreateLayout";
import RouteIndexingPolicy from "./components/seo/RouteIndexingPolicy";

const Step1Artwork = lazy(() => import("./pages/create/Step1Artwork"));
const Step2Support = lazy(() => import("./pages/create/Step2Support"));
const Step3Order = lazy(() => import("./pages/create/Step3Order"));

const StylePage = lazy(() => import('./pages/styles/StylePage'));

const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const CGV = lazy(() => import("./pages/CGV"));

const BlogListPage = lazy(() => import('./pages/blog/BlogListPage'));
const BlogArticlePage = lazy(() => import('./pages/blog/BlogArticlePage'));
const TableauPhotoPersonnalise = lazy(() => import('./pages/TableauPhotoPersonnalise'));
const PhotoEnTableau = lazy(() => import('./pages/PhotoEnTableau'));
const IdeesCadeauxPhotoPersonnalises = lazy(() => import('./pages/IdeesCadeauxPhotoPersonnalises'));
const PortraitAnimalPersonnalise = lazy(() => import('./pages/PortraitAnimalPersonnalise'));
const PortraitChienPersonnalise = lazy(() => import('./pages/PortraitChienPersonnalise'));
const PortraitChatPersonnalise = lazy(() => import('./pages/PortraitChatPersonnalise'));
const PortraitChevalPersonnalise = lazy(() => import('./pages/PortraitChevalPersonnalise'));
const PortraitFamillePersonnalise = lazy(() => import('./pages/PortraitFamillePersonnalise'));
const PortraitCouplePersonnalise = lazy(() => import('./pages/PortraitCouplePersonnalise'));
const PortraitBebePersonnalise = lazy(() => import('./pages/PortraitBebePersonnalise'));
const DecorationMuralePersonnalisee = lazy(() => import('./pages/DecorationMuralePersonnalisee'));
const CadeauAnniversairePhotoPersonnalise = lazy(() => import('./pages/CadeauAnniversairePhotoPersonnalise'));
const CadeauNoelPhotoPersonnalise = lazy(() => import('./pages/CadeauNoelPhotoPersonnalise'));
const CadeauMariagePhotoPersonnalise = lazy(() => import('./pages/CadeauMariagePhotoPersonnalise'));
const CadeauGrandsParentsPhotoPersonnalise = lazy(() => import('./pages/CadeauGrandsParentsPhotoPersonnalise'));
const CadeauDepartRetraitePhotoPersonnalise = lazy(() => import('./pages/CadeauDepartRetraitePhotoPersonnalise'));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <RouteIndexingPolicy />
            <div className="min-h-screen bg-background">
              <Header />
              <Suspense fallback={<div className="min-h-screen pt-20 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/tableau-photo-personnalise" element={<TableauPhotoPersonnalise />} />
                  <Route path="/photo-en-tableau" element={<PhotoEnTableau />} />
                  <Route path="/idees-cadeaux-photo-personnalises" element={<IdeesCadeauxPhotoPersonnalises />} />
                  <Route path="/portrait-animal-personnalise" element={<PortraitAnimalPersonnalise />} />
                  <Route path="/portrait-chien-personnalise" element={<PortraitChienPersonnalise />} />
                  <Route path="/portrait-chat-personnalise" element={<PortraitChatPersonnalise />} />
                  <Route path="/portrait-cheval-personnalise" element={<PortraitChevalPersonnalise />} />
                  <Route path="/portrait-famille-personnalise" element={<PortraitFamillePersonnalise />} />
                  <Route path="/portrait-couple-personnalise" element={<PortraitCouplePersonnalise />} />
                  <Route path="/portrait-bebe-personnalise" element={<PortraitBebePersonnalise />} />
                  <Route path="/decoration-murale-personnalisee" element={<DecorationMuralePersonnalisee />} />
                  <Route path="/cadeau-anniversaire-photo-personnalise" element={<CadeauAnniversairePhotoPersonnalise />} />
                  <Route path="/cadeau-noel-photo-personnalise" element={<CadeauNoelPhotoPersonnalise />} />
                  <Route path="/cadeau-mariage-photo-personnalise" element={<CadeauMariagePhotoPersonnalise />} />
                  <Route path="/cadeau-grands-parents-photo-personnalise" element={<CadeauGrandsParentsPhotoPersonnalise />} />
                  <Route path="/cadeau-depart-retraite-photo-personnalise" element={<CadeauDepartRetraitePhotoPersonnalise />} />
                  <Route path="/styles/:id" element={<StylePage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/create" element={<CreateLayout />}>
                    <Route index element={<Navigate to="artwork" replace />} />
                    <Route path="artwork" element={<Step1Artwork />} />
                    <Route path="support" element={<Step2Support />} />
                    <Route path="order" element={<Step3Order />} />
                  </Route>
                  <Route path="/generations" element={<Generations />} />
                  <Route path="/order-confirmation" element={<OrderConfirmation />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin/studio" element={<AdminStudio />} />
                  <Route path="/mentions-legales" element={<MentionsLegales />} />
                  <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                  <Route path="/cgv" element={<CGV />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CookieConsent />
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </AppProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  </HelmetProvider>
);

export default App;
